import { GroundType } from 'models/ground';
import React, { useMemo } from 'react';
import { Circle, Group, Line, Path, } from 'react-konva';
import {
  BASKET_AREA_OFFSET,
  BASKET_AREA_RADIUS,
  BASKET_CENTER_RADIUS,
  BASKET_FREE_TROW_AREA,
  BASKET_NO_CHARGING_AREA_RADIUS,
  BASKET_RADIUS,
  DEFAULT_PITCH_LINE_COLOR,
  DEFAULT_PITCH_STROKEWIDTH,
} from '../constants';
import { flipX, flipY, } from '../utils';
import Vertices from '../Vertices';
import { GroundGeometry } from '../../../../types/webPlayer';

function Area( props: {
  vertexA: { x: number; y: number };
  vertexC: { x: number; y: number };
  height: number;
  center: { x: number; y: number };
  scale: number;
  flipped?: boolean;
  width: number;
} ) {
  const {
    vertexA, vertexC, height, scale, flipped, center, width,
  } = props;

  const scaledBasketRadius = BASKET_RADIUS * scale;
  const scaledCenterRadius = BASKET_CENTER_RADIUS * scale;
  const scaledNoChargingRadius = BASKET_NO_CHARGING_AREA_RADIUS * scale;
  const scaledAreaRadius = BASKET_AREA_RADIUS * scale;
  const scaledAreaOffset = BASKET_AREA_OFFSET * scale;
  const scaledFreeTrowArea = BASKET_FREE_TROW_AREA * scale;

  const isReversed = vertexA.y < vertexC.y;

  const geometry = useMemo(() => ({
    line1: flipped
      ? [
        flipX({x: vertexA.x, y: center.y + scaledAreaRadius}, center),
        flipY({x: vertexA.x, y: center.y + scaledAreaRadius}, center),
        flipX({x: vertexA.x + scaledAreaOffset, y: center.y + scaledAreaRadius}, center),
        flipY({x: vertexA.x + scaledAreaOffset, y: center.y + scaledAreaRadius}, center),
      ]
      : [
        vertexA.x,
        center.y + scaledAreaRadius,
        vertexA.x + scaledAreaOffset,
        center.y + scaledAreaRadius,
      ],
    line2: flipped
      ? [
        flipX({ x: vertexA.x, y: center.y - scaledAreaRadius }, center),
        flipY({ x: vertexA.x, y: center.y - scaledAreaRadius }, center),
        flipX({ x: vertexA.x + scaledAreaOffset, y: center.y - scaledAreaRadius }, center),
        flipY({ x: vertexA.x + scaledAreaOffset, y: center.y - scaledAreaRadius }, center),
      ]
      : [
        vertexA.x,
        center.y - scaledAreaRadius,
        vertexA.x + scaledAreaOffset,
        center.y - scaledAreaRadius,
      ],
    line3: flipped
      ? [
        flipX({ x: vertexA.x, y: vertexA.y - height * (2 / 3) * (isReversed ? -1 : 1) }, center),
        flipY({ x: vertexA.x, y: vertexA.y - height * (2 / 3) * (isReversed ? -1 : 1) }, center),
        flipX({ x: vertexA.x + scaledFreeTrowArea, y: vertexA.y - height * (2 / 3) * (isReversed ? -1 : 1) }, center),
        flipY({ x: vertexA.x + scaledFreeTrowArea, y: vertexA.y - height * (2 / 3) * (isReversed ? -1 : 1) }, center),
        flipX({ x: vertexA.x + scaledFreeTrowArea, y: vertexA.y - height / 3 * (isReversed ? -1 : 1) }, center),
        flipY({ x: vertexA.x + scaledFreeTrowArea, y: vertexA.y - height / 3 * (isReversed ? -1 : 1) }, center),
        flipX({ x: vertexA.x, y: vertexA.y - height / 3 * (isReversed ? -1 : 1) }, center),
        flipY({ x: vertexA.x, y: vertexA.y - height / 3 * (isReversed ? -1 : 1) }, center),
      ]
      : [
        vertexA.x,
        vertexA.y - height * (2 / 3) * (isReversed ? -1 : 1),
        vertexA.x + scaledFreeTrowArea,
        vertexA.y - height * (2 / 3) * (isReversed ? -1 : 1),
        vertexA.x + scaledFreeTrowArea,
        vertexA.y - height / 3 * (isReversed ? -1 : 1),
        vertexA.x,
        vertexA.y - height / 3 * (isReversed ? -1 : 1),
      ],
    path1: {
      x: flipped ? vertexC.x - scaledAreaOffset : vertexA.x + scaledAreaOffset,
      y: center.y,
      data: `M 0 ${-scaledAreaRadius} A ${scaledAreaRadius} ${scaledAreaRadius} 0 0 1 0 ${scaledAreaRadius}`
    },
    path2: {
      x: flipped ? vertexC.x - scaledFreeTrowArea + 2 : vertexA.x + scaledFreeTrowArea,
      y: center.y,
      data: `M 0 ${-scaledCenterRadius} A ${scaledCenterRadius} ${scaledCenterRadius} 0 0 1 0 ${scaledCenterRadius}`,
    },
    path3: {
      x: flipped ? vertexC.x - scaledAreaOffset : vertexA.x + scaledAreaOffset,
      y: center.y,
      data: `M 0 ${-scaledNoChargingRadius} A ${scaledNoChargingRadius} ${scaledNoChargingRadius} 0 0 1 0 ${scaledNoChargingRadius}`,
    },
    circle: {
      x: flipped ? vertexC.x - scaledAreaOffset : vertexA.x + scaledAreaOffset,
      y: center.y,
      radius: scaledBasketRadius < 0 ? scaledBasketRadius * -1 : scaledBasketRadius,
    },
  }), [vertexA.x, vertexA.y, vertexC.x, vertexC.y, flipped, width, height, center]);

  return (
    <Group>
      <Line
        points={geometry.line1}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Path
        x={geometry.path1.x}
        y={geometry.path1.y}
        data={geometry.path1.data}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        rotation={flipped ? 180 : 0}
      />
      <Line
        points={geometry.line2}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      {/* Linea dei 3 secondi */}
      <Line
        points={geometry.line3}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      {/* Semicerchio del tiro libero */}
      <Path
        x={geometry.path2.x}
        y={geometry.path2.y}
        data={geometry.path2.data}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        rotation={flipped ? 180 : 0}
      />
      {/* Semicerchio del canestro */}
      <Path
        x={geometry.path3.x}
        y={geometry.path3.y}
        data={geometry.path3.data}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        rotation={flipped ? 180 : 0}
      />
      {/* Canestro */}
      <Circle
        x={geometry.circle.x}
        y={geometry.circle.y}
        radius={geometry.circle.radius}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
    </Group>
  );
}

export default function Basket( props: {
  groundData: GroundType;
  scale: number;
  vertices: boolean;
  groundGeometry: GroundGeometry;
} ) {
  const {groundData, vertices, groundGeometry} = props;

  const geometry = useMemo(() => {
    const {
      scaledDims, center, scale, vertex: vertexPoints,
    } = groundGeometry;
    const {height, width} = scaledDims;

    const scaledCenterRadius = BASKET_CENTER_RADIUS * scale;

    return {
      vertexPoints,
      scale,
      center,
      scaledDims,
      width,
      height,
      line1: [
        vertexPoints.vertexA.x, vertexPoints.vertexA.y,
        vertexPoints.vertexC.x, vertexPoints.vertexA.y,
        vertexPoints.vertexC.x, vertexPoints.vertexC.y,
        vertexPoints.vertexA.x, vertexPoints.vertexC.y,
      ],
      line2: [
        center.x, vertexPoints.vertexA.y,
        center.x, vertexPoints.vertexC.y,
      ],
      circle1: {
        x: center.x,
        y: center.y,
        radius: scaledCenterRadius < 0 ? scaledCenterRadius * -1 : scaledCenterRadius,
      },
    };
  }, [groundGeometry]);

  return (
    <Group>
      {/* border */}
      <Line
        fill={groundData.groundSurfaceColor ? groundData.groundSurfaceColor : '#00000000'}
        points={geometry.line1}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        closed
      />
      {/* left area */}
      <Area
        vertexA={geometry.vertexPoints.vertexA}
        vertexC={geometry.vertexPoints.vertexC}
        width={geometry.width}
        height={geometry.height}
        center={geometry.center}
        scale={geometry.scale}
      />
      {/* rightarea */}
      <Area
        vertexA={geometry.vertexPoints.vertexA}
        vertexC={geometry.vertexPoints.vertexC}
        width={geometry.width}
        height={geometry.height}
        center={geometry.center}
        scale={geometry.scale}
        flipped
      />
      {/* halfcourt */}
      <Line
        points={geometry.line2}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Circle
        x={geometry.circle1.x}
        y={geometry.circle1.y}
        radius={geometry.circle1.radius}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />

      {vertices ? <Vertices A={geometry.vertexPoints.vertexA} C={geometry.vertexPoints.vertexC}/> : null}
    </Group>
  );
}
